import { useEffect, useMemo, useState } from 'react';
import { differenceInDays, startOfDay } from 'date-fns';
import { stanNotify } from '@/lib/stanNotify';
import { DEFAULT_APPOINTMENT_DURATION_MINUTES, DEFAULT_TIME_VALUES } from '../constants/appointmentData';
import { getLocalDateString, minutesToTimeString, TIME_OPTIONS, timeStringToMinutes } from '@/lib/utils/timeUtils';
import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';

interface UseBlockTimeModalParams {
    isOpen: boolean;
    selectedDate: Date;
    blockedTimeId?: string;
    initialStartTime?: string;
    initialEndTime?: string;
    onSave: (blockTime: {
        id?: string;
        startDate: string;
        startTime: string;
        endDate: string;
        endTime: string;
        silent?: boolean;
    }) => Promise<void>;
    onClose: () => void;
}

export function useBlockTimeModal({
    isOpen,
    selectedDate,
    blockedTimeId,
    initialStartTime,
    initialEndTime,
    onSave,
    onClose,
}: UseBlockTimeModalParams) {
    const isEditMode = !!blockedTimeId;

    const [startDate, setStartDate] = useState<Date | undefined>(selectedDate);
    const [endDate, setEndDate] = useState<Date | undefined>(selectedDate);
    const [startTimeMinutes, setStartTimeMinutes] = useState<number>(
        initialStartTime ? timeStringToMinutes(initialStartTime) : DEFAULT_TIME_VALUES.BLOCK_START
    );
    const [endTimeMinutes, setEndTimeMinutes] = useState<number>(
        initialEndTime ? timeStringToMinutes(initialEndTime) : DEFAULT_TIME_VALUES.BLOCK_END
    );

    // Update dates and times when modal opens or selectedDate changes
    useEffect(() => {
        if (isOpen) {
            setStartDate(selectedDate);
            setEndDate(selectedDate);

            if (initialStartTime) {
                setStartTimeMinutes(timeStringToMinutes(initialStartTime));
            }
            if (initialEndTime) {
                setEndTimeMinutes(timeStringToMinutes(initialEndTime));
            }
        }
    }, [isOpen, selectedDate, initialStartTime, initialEndTime]);

    const currentStartTime = useMemo(() => {
        const option = TIME_OPTIONS.find(opt => opt.value === String(startTimeMinutes));
        return option || TIME_OPTIONS[0];
    }, [startTimeMinutes]);

    const currentEndTime = useMemo(() => {
        const option = TIME_OPTIONS.find(opt => opt.value === String(endTimeMinutes));
        return option || TIME_OPTIONS[0];
    }, [endTimeMinutes]);

    // Filter end time options to only show times after the start time
    const filteredEndTimeOptions = useMemo(() => {
        return TIME_OPTIONS.filter(option => Number(option.value) > startTimeMinutes);
    }, [startTimeMinutes]);

    // Validate that start time is before end time
    const isValidTimeRange = startTimeMinutes < endTimeMinutes;
    const isInvalid = !isValidTimeRange;

    const handleStartTimeSelect = (value: DropdownValue) => {
        const newStartTime = Number(value.value);
        setStartTimeMinutes(newStartTime);

        // Auto-set end time to default appointment duration after start time
        const defaultEndTime = newStartTime + DEFAULT_APPOINTMENT_DURATION_MINUTES;

        // Find the exact time option or the closest one after the calculated time
        const endTimeOption =
            TIME_OPTIONS.find(opt => Number(opt.value) === defaultEndTime) ||
            TIME_OPTIONS.find(opt => Number(opt.value) > defaultEndTime);

        if (endTimeOption) {
            setEndTimeMinutes(Number(endTimeOption.value));
        }
    };

    const handleEndTimeSelect = (value: DropdownValue) => {
        setEndTimeMinutes(Number(value.value));
    };

    const handleStartDateSelect = (date: Date | undefined) => {
        setStartDate(date);
        // If end date is before start date, update it
        if (date && endDate && date > endDate) {
            setEndDate(date);
        }
    };

    const handleEndDateSelect = (date: Date | undefined) => {
        setEndDate(date);
    };

    const handleSave = async () => {
        if (isInvalid || !startDate || !endDate) return;

        const startDateTime = startOfDay(startDate);
        const endDateTime = startOfDay(endDate);
        const dayCount = differenceInDays(endDateTime, startDateTime) + 1;
        const isMultipleDays = dayCount > 1;

        try {
            // onSave is React Query's mutateAsync, which already returns a Promise
            await onSave({
                ...(isEditMode && blockedTimeId && { id: blockedTimeId }),
                startDate: getLocalDateString(startDate),
                startTime: minutesToTimeString(startTimeMinutes),
                endDate: getLocalDateString(endDate),
                endTime: minutesToTimeString(endTimeMinutes),
                silent: isMultipleDays, // Prevent notification for multi-day to show custom message
            });

            // Show custom notification for multi-day blocks
            if (isMultipleDays) {
                stanNotify({
                    type: 'success',
                    title: isEditMode ? 'Time Block Updated' : 'Time Blocked Successfully',
                    text: isEditMode
                        ? `Successfully updated to ${dayCount} day${dayCount > 1 ? 's' : ''}!`
                        : `Successfully blocked ${dayCount} day${dayCount > 1 ? 's' : ''}!`,
                });
            }

            onClose();
        } catch (error) {
            console.error('Error blocking time:', error);
            stanNotify({
                type: 'error',
                title: isEditMode ? 'Failed to Update Time Block' : 'Failed to Block Time',
                text: 'There was an error processing the time block.',
            });
        }
    };

    return {
        // State
        startDate,
        endDate,
        startTimeMinutes,
        endTimeMinutes,
        isEditMode,

        // Computed values
        currentStartTime,
        currentEndTime,
        filteredEndTimeOptions,
        isInvalid,

        // Handlers
        handleStartDateSelect,
        handleEndDateSelect,
        handleStartTimeSelect,
        handleEndTimeSelect,
        handleSave,
    };
}
